import { FC, PropsWithChildren, useEffect } from 'react';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { observer } from 'mobx-react';
import { HashRouter, Redirect, Route } from 'react-router-dom';
import { DocumentTitle } from '@servicetitan/document-title';
import { EHStore } from '../stores/eh.store';
import { MainModule } from '../../main/main.module';
import { AccountSettingsModule } from '../../account-settings/account-settings';
import { EHModuleWrapper } from './eh-module-wrapper';
import { useConfigureDatadog } from '../hooks';
import { EhWidgetModules } from './eh-widget-modules';
import { SettingsStore } from '../stores/settings.store';
import { FF_CLIENT_SIDE_ID_SETTING_KEY } from '../constants';
import { HttpClient } from '../api/http-client';
import { EnterpriseHubApi } from '../api/eh.api';
import { LDProvider } from '@servicetitan/launchdarkly-service';
import { Spinner } from '@servicetitan/design-system';
import { TermsOfUseModule } from '../../terms-of-use/terms-of-use.module';

export const AppWrapper: FC = provide({
    singletons: [HttpClient, EnterpriseHubApi, EHStore, SettingsStore],
})(
    observer(() => {
        const [{ routeModules, initialized, isAdmin, networkId, isTermsOfUseAccepted, init }, { getSetting }] =
            useDependencies(EHStore, SettingsStore);

        useEffect(() => {
            init();
        }, [init]);

        if (!initialized) {
            return <Spinner />;
        }

        if (!isTermsOfUseAccepted) {
            return (
                <LDProvider clientSideID={getSetting(FF_CLIENT_SIDE_ID_SETTING_KEY)} projectName="EnterpriseHub" context={isAdmin ? {} : { kind: 'network', key: `${networkId}` }}>
                    <HashRouter>
                        <AppUnWrapped>
                            <Route path="/account-settings" component={AccountSettingsModule} />
                            <Route path="/" component={TermsOfUseModule} />
                            <Redirect to="/" />
                        </AppUnWrapped>
                    </HashRouter>
                </LDProvider>
            )
        }

        return (
            <LDProvider clientSideID={getSetting(FF_CLIENT_SIDE_ID_SETTING_KEY)} projectName="EnterpriseHub" context={isAdmin ? {} : { kind: 'network', key: `${networkId}` }}>
                <HashRouter>
                    <AppUnWrapped>
                        <Route path="/" exact component={MainModule} />
                        <Route path="/account-settings" component={AccountSettingsModule} />
                        {routeModules.map(m => {
                            return (
                                <Route path={m.basePath} key={`module-route-${m.id}`}>
                                    <DocumentTitle title={m.name} />
                                    <EHModuleWrapper module={m} />
                                </Route>
                            );
                        })}
                        <EhWidgetModules />
                    </AppUnWrapped>
                </HashRouter>
            </LDProvider>
        );
    }),
);

const AppUnWrapped: FC<PropsWithChildren> = ({ children }) => {
    useConfigureDatadog();

    return children;
};
